import React, { useEffect, useState } from 'react'; 
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  IconButton,
  TableRow,
  TablePagination,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  Grid
} from '@mui/material';
import axios from 'axios';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { gridSpacing } from 'config.js';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

const apiUrl = 'https://api.academy.eurekagroup.id/progress';
const retakeApiUrl = `${apiUrl}/retake`;

const ProgressPage = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const fetchProgressData = async () => {
      try {
        const response = await axios.get(apiUrl);
        console.log(response.data); // Check the data structure here
        setData(response.data);
        setFilteredData(response.data);
      } catch (error) {
        console.error('Error fetching progress data:', error);
      }
    };
  
    fetchProgressData();
  }, []);

  const handleViewProgress = (idEmployee, idCourse) => {
    navigate(`/academy/view-progress/${idEmployee}/${idCourse}`);
  };  

  const handleSearchChange = (event) => setSearchText(event.target.value);
  const handleEmployeeFilterChange = (event) => setEmployeeFilter(event.target.value);
  const handleCourseFilterChange = (event) => setCourseFilter(event.target.value);

  useEffect(() => {
    const filtered = data.filter((item) => {
      const matchesEmployee = employeeFilter ? item.employeeName === employeeFilter : true;
      const matchesCourse = courseFilter ? item.id_course === courseFilter : true;
      const matchesSearch = (item.employeeName || '').toLowerCase().includes(searchText.toLowerCase()) ||
                            (item.courseTitle || '').toLowerCase().includes(searchText.toLowerCase());
      return matchesEmployee && matchesCourse && matchesSearch;
    });
    setFilteredData(filtered);
  }, [employeeFilter, courseFilter, searchText, data]);  

  const handleRetake = async (employeeId, courseId) => {
    // Menampilkan SweetAlert untuk konfirmasi
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to reset the progress for this course?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
    });
  
    // Jika pengguna mengklik OK
    if (result.isConfirmed) {
      // Pastikan courseId adalah ID yang valid, bukan nama kursus
      const payload = {
        id_employee: employeeId,
        id_course: courseId,  // pastikan courseId adalah ObjectId yang valid
      };
  
      try {
        const response = await fetch(retakeApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
  
        if (!response.ok) {
          throw new Error("Failed to reset course progress");
        }
  
        const data = await response.json();
        console.log("Success:", data);
  
        // Menutup modal (asumsi Anda memiliki modal terbuka)
        // Menampilkan alert sukses
        Swal.fire({
          title: 'Success!',
          text: 'Course progress has been reset.',
          icon: 'success',
          confirmButtonText: 'OK',
        });
  
        // Lakukan pengambilan data atau pembaruan sesuai kebutuhan
        // Misalnya, refresh data atau tutup modal jika perlu.
  
      } catch (error) {
        console.error("Error:", error);
  
        // Menangani error dengan pesan khusus
        if (error.message === "Course not found") {
          const id_course = courseId;
          const id_employee = employeeId;
  
          console.log("Course not found for the following details:");
          console.log("Course ID:", id_course);
          console.log("Employee ID:", id_employee);
        }
      }
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredData.map((item, index) => ({
        No: index + 1,
        Employee: item.employeeName,
        Course: item.courseTitle,
        'Original Progress': item.originalProgress,
        'Original Score': item.originalScore,
        'Retake Progress': item.retakeProgress,
        'Retake Score': item.retakeScore,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Progress Data');
    XLSX.writeFile(workbook, 'Progress_Data.xlsx');
  };

  return (
    <>
      <Grid container spacing={gridSpacing}>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <h1 style={{ color: '#2d3436', fontFamily: 'Poppins', fontSize: '20px' }}>
                    Progress Management
                  </h1><Box display="flex" gap={2}>
                    <TextField
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      value={searchText}
                      onChange={handleSearchChange}
                      sx={{ borderRadius: '8px', backgroundColor: '#f5f5f5' }}
                    />
                    <Button variant="contained" color="success" onClick={handleExportExcel}>
                      Export Excel
                    </Button>
                  </Box>
                </Box>
              }
            />
            <CardContent>
              <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth>
                    <Select
                      value={employeeFilter}
                      onChange={handleEmployeeFilterChange}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Employee' }}
                    >
                      <MenuItem value="">All Employees</MenuItem>
                      {[...new Set(data.map(item => item.employeeName))].map((employee) => (
                        <MenuItem key={employee} value={employee}>{employee}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                <FormControl fullWidth>
  <Select
    value={courseFilter}
    onChange={handleCourseFilterChange}
    displayEmpty
    inputProps={{ 'aria-label': 'Courses' }}
  >
    <MenuItem value="">All Courses</MenuItem>
    {/* Memfilter dan menampilkan courseTitle berdasarkan id_course */}
    {[...new Set(data.flatMap(item => item.id_course ? [item.id_course] : []))]
      .map((courseId) => {
        const course = data.find(item => item.id_course === courseId); // Temukan course berdasarkan id_course
        return course ? (
          <MenuItem key={course.id_course} value={course.id_course}>
            {course.courseTitle} {/* Tampilkan courseTitle */}
          </MenuItem>
        ) : null;
      })}
  </Select>
</FormControl>
                </Grid>
              </Grid>
              <TableContainer>
  <Table>
    <TableHead>
      <TableRow>
        <TableCell>No</TableCell>
        <TableCell>Employee</TableCell>
        <TableCell>Course</TableCell>
        <TableCell>Original Progress</TableCell>
        <TableCell>Original Score</TableCell>
        <TableCell>Retake Progress</TableCell>
        <TableCell>Retake Score</TableCell>
        <TableCell>Original Grade</TableCell>
        <TableCell>Retake Grade</TableCell>
        <TableCell>Action</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {filteredData
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((progress, index) => (
          <TableRow key={progress.id_employee}>
            {/* Nomor */}
            <TableCell>{page * rowsPerPage + index + 1}</TableCell>

            {/* Nama Karyawan */}
            <TableCell>{progress.employeeName}</TableCell>

            {/* Judul Kursus */}
            <TableCell>{progress.courseTitle}</TableCell>

            {/* Progress Asli */}
            <TableCell>{progress.originalProgress}</TableCell>

            {/* Skor Asli */}
            <TableCell>{progress.originalScore}</TableCell>

            {/* Progress Ulang */}
            <TableCell>{progress.retakeProgress}</TableCell>

            {/* Skor Ulang */}
            <TableCell>{progress.retakeScore}</TableCell>


            <TableCell>{progress.categoryOriginal || "Tidak ada data"}</TableCell>
            
            <TableCell>{progress.categoryRetake || "Tidak ada data"}</TableCell>

            {/* Aksi */}
            <TableCell>
            <IconButton
                  color="primary"
                  onClick={() =>
                    handleViewProgress(progress.id_employee, progress.id_course)
                  }
                >
                  <VisibilityIcon />
                </IconButton>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  handleRetake(progress.id_employee, progress.id_course)
                }
              >
                Retake
              </Button>
            </TableCell>
          </TableRow>
        ))}
    </TableBody>
  </Table>
</TableContainer>

              <Box display="flex" justifyContent="flex-end" mt={2}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  count={filteredData.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default ProgressPage;
